// pages/student/components/SideBar.js
import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Box,
  Avatar,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Paper,
  Chip,
  Stack,
  Tooltip,
  Badge,
} from "@mui/material";
import {
  Dashboard,
  School,
  MenuBook,
  EmojiEvents,
  Settings,
  Logout,
  ChevronRight,
  Person,
  TrendingUp,
  Star,
} from "@mui/icons-material";
import "../../../styles/SideBar.css";

const Menu = [
  {
    icon: <MenuBook />,
    title: "My Courses",
    link: "/mycourses",
    color: "#2BAD65",
  },
  {
    icon: <School />,
    title: "Courses",
    link: "/courses",
    color: "#A0EF48",
  },
  {
    icon: <EmojiEvents />,
    title: "Certificates",
    link: "/certificates",
    color: "#FFD93D",
  },
  {
    icon: <Settings />,
    title: "Settings",
    link: "/studentsetting",
    color: "#45B7D1",
  },
];

const SideBar = () => {
  const nav = useNavigate();
  const [userInfo, setUserInfo] = useState(null);
  const [activeLink, setActiveLink] = useState("/mycourses");

  useEffect(() => {
    const user = localStorage.getItem("user");
    if (user) {
      try {
        const data = JSON.parse(user);
        setUserInfo({
          firstname: data.firstname || "",
          lastname: data.lastname || "",
          email: data.email || "",
          avatar: data.avatar || "",
        });
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    } else {
      nav("/");
    }
  }, [nav]);

  const handleLogOut = () => {
    localStorage.removeItem("user");
    nav("/");
  };

  const getInitials = () => {
    if (!userInfo) return "NA";
    const first = userInfo.firstname?.charAt(0) || "";
    const last = userInfo.lastname?.charAt(0) || "";
    return `${first}${last}`.toUpperCase() || "NA";
  };

  const fullName = userInfo
    ? `${userInfo.firstname} ${userInfo.lastname}`
    : "User";

  return (
    <Box
      className="sidebar-container"
      sx={{
        width: { xs: 280, md: 280 },
        minHeight: "100vh",
        backgroundColor: "#F7F8F2",
        borderRight: "1px solid rgba(43, 173, 101, 0.08)",
        display: "flex",
        flexDirection: "column",
        position: "sticky",
        top: 0,
        height: "100vh",
        overflow: "hidden",
        boxShadow: "2px 0 20px rgba(43, 173, 101, 0.04)",
      }}
    >
      {/* User Profile Section */}
      <Box
        sx={{
          p: 3,
          textAlign: "center",
          borderBottom: "1px solid rgba(43, 173, 101, 0.06)",
        }}
      >
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          badgeContent={
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: "50%",
                backgroundColor: "#2BAD65",
                border: "2px solid #F7F8F2",
              }}
            />
          }
        >
          <Avatar
            src={userInfo?.avatar || ""}
            sx={{
              width: 80,
              height: 80,
              margin: "0 auto",
              backgroundColor: "#2BAD65",
              fontSize: "2rem",
              fontWeight: 700,
              color: "#ffffff",
              boxShadow: "0 4px 20px rgba(43, 173, 101, 0.2)",
              transition: "transform 0.3s ease",
              "&:hover": {
                transform: "scale(1.05)",
              },
            }}
          >
            {getInitials()}
          </Avatar>
        </Badge>

        <Typography
          variant="h6"
          sx={{
            mt: 2,
            fontWeight: 700,
            color: "#1a1a1a",
            fontSize: "1.1rem",
          }}
        >
          {fullName}
        </Typography>

        <Typography
          variant="caption"
          sx={{
            color: "rgba(26, 26, 26, 0.5)",
            display: "block",
            mb: 1,
          }}
        >
          {userInfo?.email || ""}
        </Typography>

        <Chip
          label="Student"
          size="small"
          sx={{
            backgroundColor: "rgba(43, 173, 101, 0.1)",
            color: "#2BAD65",
            fontWeight: 600,
            fontSize: "0.7rem",
            border: "1px solid rgba(43, 173, 101, 0.15)",
          }}
        />

        {/* Stats */}
        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          sx={{ mt: 2 }}
        >
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "#2BAD65", fontSize: "1rem" }}
            >
              12
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "rgba(26, 26, 26, 0.4)" }}
            >
              Courses
            </Typography>
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "#FFD93D", fontSize: "1rem" }}
            >
              8
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "rgba(26, 26, 26, 0.4)" }}
            >
              Completed
            </Typography>
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "#FF6B6B", fontSize: "1rem" }}
            >
              4
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "rgba(26, 26, 26, 0.4)" }}
            >
              In Progress
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Menu Items */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          py: 2,
          px: 1.5,
          "&::-webkit-scrollbar": {
            width: 4,
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(43, 173, 101, 0.2)",
            borderRadius: 10,
          },
        }}
      >
        <Typography
          variant="caption"
          sx={{
            px: 2,
            py: 1,
            color: "rgba(26, 26, 26, 0.3)",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: 1,
            display: "block",
            fontSize: "0.65rem",
          }}
        >
          Main Menu
        </Typography>

        <List sx={{ p: 0 }}>
          {Menu.map((item, index) => (
            <ListItem key={index} sx={{ p: 0.5 }}>
              <NavLink
                to={item.link}
                style={({ isActive }) => ({
                  textDecoration: "none",
                  width: "100%",
                })}
              >
                {({ isActive }) => (
                  <ListItemButton
                    sx={{
                      borderRadius: 2,
                      py: 1.2,
                      px: 2,
                      backgroundColor: isActive
                        ? "rgba(43, 173, 101, 0.08)"
                        : "transparent",
                      border: isActive
                        ? "1px solid rgba(43, 173, 101, 0.15)"
                        : "1px solid transparent",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: "rgba(43, 173, 101, 0.05)",
                        transform: "translateX(4px)",
                      },
                      "&:active": {
                        transform: "scale(0.98)",
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 40,
                        color: isActive ? "#2BAD65" : "rgba(26, 26, 26, 0.4)",
                        transition: "color 0.3s ease",
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.title}
                      sx={{
                        "& .MuiTypography-root": {
                          fontWeight: isActive ? 600 : 500,
                          color: isActive ? "#1a1a1a" : "rgba(26, 26, 26, 0.6)",
                          fontSize: "0.9rem",
                        },
                      }}
                    />
                    {isActive && (
                      <ChevronRight
                        sx={{
                          color: "#2BAD65",
                          fontSize: 20,
                        }}
                      />
                    )}
                  </ListItemButton>
                )}
              </NavLink>
            </ListItem>
          ))}
        </List>

        <Divider sx={{ my: 2, backgroundColor: "rgba(43, 173, 101, 0.06)" }} />

        <Typography
          variant="caption"
          sx={{
            px: 2,
            py: 1,
            color: "rgba(26, 26, 26, 0.3)",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: 1,
            display: "block",
            fontSize: "0.65rem",
          }}
        >
          Account
        </Typography>

        {/* Logout Button */}
        <ListItem sx={{ p: 0.5 }}>
          <ListItemButton
            onClick={handleLogOut}
            sx={{
              borderRadius: 2,
              py: 1.2,
              px: 2,
              backgroundColor: "rgba(255, 107, 107, 0.04)",
              border: "1px solid rgba(255, 107, 107, 0.08)",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: "rgba(255, 107, 107, 0.08)",
                transform: "translateX(4px)",
                borderColor: "rgba(255, 107, 107, 0.2)",
              },
              "&:active": {
                transform: "scale(0.98)",
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 40,
                color: "#FF6B6B",
              }}
            >
              <Logout />
            </ListItemIcon>
            <ListItemText
              primary="Logout"
              sx={{
                "& .MuiTypography-root": {
                  fontWeight: 500,
                  color: "#FF6B6B",
                  fontSize: "0.9rem",
                },
              }}
            />
          </ListItemButton>
        </ListItem>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: 2,
          borderTop: "1px solid rgba(43, 173, 101, 0.06)",
          textAlign: "center",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: "rgba(26, 26, 26, 0.2)",
            fontSize: "0.6rem",
          }}
        >
          Wavepalm Techub v2.0
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 0.5,
            mt: 0.5,
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor: "#2BAD65",
            }}
          />
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor: "#A0EF48",
            }}
          />
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor: "#FF6B6B",
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default SideBar;
